import { afterNextRender, Injector } from '@angular/core';

const INVALID_QUERY = '.is-invalid:not([type="hidden"]):not(disabled):not(readonly)';
const INVALID_DIV_QUERY = 'input:not([type="hidden"]):not(disabled):not(readonly)';

/**
 * Focuses the first invalid form input in the DOM after validation occurs.
 *
 * If an Angular {@link Injector} is provided, the focus is deferred until
 * the next render cycle using `afterNextRender()`.
 * Automatically disabled during certain Cypress test runs to avoid
 * interfering with test behavior.
 *
 * @param injector Optional injector used to defer focusing until after render.
 */
export function focusFirstInvalidInput(injector?: Injector): void {
  // Only focus if NOT in Cypress test or if the cypressAuthBypass flag is not set, as this causes issues with cypress tests that are not related to form validation.
  if ((window as any).cypressAuthBypass && (window as any).Cypress) return;

  function focus(): void {
    const invalidInput = document.querySelector<HTMLElement>(INVALID_QUERY);
    if (!invalidInput) return;

    const actualInputToFocus = invalidInput.tagName === 'DIV' ? invalidInput.querySelector<HTMLElement>(INVALID_DIV_QUERY) : invalidInput;
    if (!actualInputToFocus) return;

    if (document.activeElement === actualInputToFocus) return;
    actualInputToFocus.focus();
  }

  if (injector) {
    afterNextRender(() => focus(), { injector });
    return;
  }

  focus();
}

/**
 * Returns the id of the currently focused DOM element.
 *
 * @returns The id of document.activeElement, or an empty string if none is set.
 */
export function getFocusedElementId(): string {
  return document.activeElement.id || '';
}
